import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import { Modal, Button } from '../common';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// 스타일 상수
const buttonStyles = {
  base: "px-3 py-1.5 rounded-3xl min-w-[80px] text-center cursor-pointer transition-all duration-200 text-sm font-medium",
  correct: {
    active: "bg-green-500 text-white shadow-md hover:bg-green-600",
    inactive: "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200"
  },
  incorrect: {
    active: "bg-red-500 text-white shadow-md hover:bg-red-600",
    inactive: "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
  },
  batch: {
    correct: "bg-green-50 text-green-700 hover:bg-green-100 border border-green-200",
    incorrect: "bg-red-50 text-red-700 hover:bg-red-100 border border-red-200"
  }
};

function ResultInputModal({ isOpen, onClose, examId, stampId, onRefresh, isConfirmMode }) {
  const { getAccessToken } = useAuth();
  const [problems, setProblems] = useState([]);
  const [results, setResults] = useState({});
  const [isAlreadySubmitted, setIsAlreadySubmitted] = useState(false);
  const [submittedResults, setSubmittedResults] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [solutions, setSolutions] = useState({});

  useEffect(() => {
    if (isOpen && examId) {
      setError(null);
      setIsSubmitting(false);
      setIsAlreadySubmitted(false);
      setProblems([]);
      setResults({});
      setSubmittedResults([]);
      setSolutions({});
      fetchExamDetails();
    }
  }, [isOpen, examId]);

  const fetchExamDetails = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/result`, {
        headers: { 'Authorization': token }
      });
      const data = await response.json();

      if (data.status === 'success') {
        const problemsList = data.data.problem_results.map(problem => ({
          problem_id: problem.problem_id,
          solution: problem.answer
        }));
        setProblems(problemsList);

        const initialResults = {};
        data.data.problem_results.forEach(problem => {
          if (problem.result !== null) {
            initialResults[problem.problem_id] = problem.result;
          }
        });
        setResults(initialResults);

        const solutionsMap = {};
        data.data.problem_results.forEach(problem => {
          if (problem.answer) {
            solutionsMap[problem.problem_id] = problem.answer;
          }
        });
        setSolutions(solutionsMap);

        const isAllAnswered = data.data.problem_results.every(problem => problem.result !== null);
        setIsAlreadySubmitted(isAllAnswered);
      }
    } catch (error) {
      console.error('시험 상세 정보 조회 실패:', error);
      setError('시험 정보를 불러오는데 실패했습니다.');
    }
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      const token = await getAccessToken();
      const examResultList = Object.entries(results).map(([problem_id, result]) => ({
        problem_id: parseInt(problem_id),
        result
      }));

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/result`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ exam_result_list: examResultList })
      });

      if (response.ok) {
        onRefresh?.(examId);
        onClose();
      }
    } catch (error) {
      console.error('결과 제출 실패:', error);
      setError('결과 제출에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmResults = async () => {

    try {
      setIsConfirming(true);

      // 1. 먼저 현재 결과를 저장
      const token = await getAccessToken();
      const examResultList = Object.entries(results).map(([problem_id, result]) => ({
        problem_id: parseInt(problem_id),
        result
      }));

      const saveResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/result`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ exam_result_list: examResultList })
      });

      if (!saveResponse.ok) {
        throw new Error('결과 저장에 실패했습니다.');
      }

      // 2. 결과 저장이 성공하면 확정 API 호출
      const confirmResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/exams/${stampId}/${examId}/close`, {
        method: 'POST',
        headers: {
          'Authorization': token
        }
      });

      if (!confirmResponse.ok) {
        const errorData = await confirmResponse.json();
        throw new Error(errorData.message || '결과 확정에 실패했습니다.');
      }

      onRefresh?.(examId);
      onClose();
    } catch (error) {
      console.error('결과 확정 실패:', error);
      setError(error.message);
    } finally {
      setIsConfirming(false);
    }
  };

  // 모든 문제가 선택되었는지 확인하는 함수
  const isAllProblemsAnswered = () => {
    return problems.every(problem => results[problem.problem_id] !== undefined);
  };

  // 문제 개수와 답변 개수 계산
  const totalProblems = problems.length;
  const answeredProblems = Object.keys(results).length;
  const correctCount = Object.values(results).filter(r => r === true).length;
  const incorrectCount = Object.values(results).filter(r => r === false).length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="flex items-center gap-3">
          <span>
            {isConfirmMode ? '결과 확정' : (isAlreadySubmitted ? '결과 수정' : '문제 결과 입력')}
          </span>
          <div className="flex items-center gap-2 text-sm font-normal text-gray-600">
            <span className="flex items-center gap-1">
              <CheckCircle size={14} className="text-green-500" />
              <span>{correctCount}</span>
            </span>
            <span className="text-gray-400">/</span>
            <span className="flex items-center gap-1">
              <XCircle size={14} className="text-red-500" />
              <span>{incorrectCount}</span>
            </span>
            <span className="text-gray-400">/</span>
            <span className="text-gray-500">{totalProblems}</span>
          </div>
        </div>
      }
      size="lg"
      showCloseButton={true}
      closeOnBackdropClick={false}
      closeOnEsc={true}
      preventScroll={true}
      bodyClassName="p-0"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting || isConfirming}
          >
            취소
          </Button>
          <Button
            variant={isConfirmMode ? "danger" : "primary"}
            onClick={isConfirmMode ? handleConfirmResults : handleSubmit}
            disabled={!isAllProblemsAnswered() || isSubmitting || isConfirming}
            loading={isSubmitting || isConfirming}
            className={isConfirmMode ? "!bg-blue-600 !text-white hover:!bg-blue-700" : ""}
          >
            {isSubmitting || isConfirming ? '처리 중...' : isConfirmMode ? '결과 확정' : '저장'}
          </Button>
        </>
      }
    >
      {/* 확정 모드 경고 메시지 */}
      {isConfirmMode && !error && (
        <div className="mx-4 mb-4 flex items-center gap-2 rounded-xl bg-blue-50 px-4 py-3 text-sm text-blue-700 border border-blue-200">
          <AlertCircle size={16} className="shrink-0" />
          <span className="font-medium">결과 확정 후에는 수정이 불가능합니다. 신중하게 검토 후 확정해주세요.</span>
        </div>
      )}

      {/* 에러 메시지 */}
      {error && (
        <div className="mx-4 mt-4 mb-2 flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 border border-red-200">
          <AlertCircle size={16} className="shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* 일괄 처리 버튼 */}
      <div className="mx-4 mb-4 flex gap-2">
        <button
          onClick={() => {
            const newResults = {};
            problems.forEach(problem => {
              newResults[problem.problem_id] = true;
            });
            setResults(newResults);
          }}
          className={`${buttonStyles.base} ${buttonStyles.batch.correct} flex flex-1 items-center justify-center gap-1.5`}
        >
          <CheckCircle size={14} />
          전체 정답
        </button>
        <button
          onClick={() => {
            const newResults = {};
            problems.forEach(problem => {
              newResults[problem.problem_id] = false;
            });
            setResults(newResults);
          }}
          className={`${buttonStyles.base} ${buttonStyles.batch.incorrect} flex flex-1 items-center justify-center gap-1.5`}
        >
          <XCircle size={14} />
          전체 오답
        </button>
      </div>

      {/* 테이블 */}
      <div className="overflow-y-auto" style={{ maxHeight: 'calc(70vh - 240px)' }}>
        <table className="w-full">
          <thead className="sticky top-0 bg-gray-50 border-y border-gray-200">
            <tr>
              <th className="w-16 px-3 py-3 text-center text-xs font-semibold text-gray-700">번호</th>
              <th className="w-20 px-3 py-3 text-center text-xs font-semibold text-gray-700">문제 ID</th>
              <th className="w-40 px-3 py-3 text-center text-xs font-semibold text-gray-700">채점</th>
              <th className="flex-1 px-3 py-3 text-center text-xs font-semibold text-gray-700">정답</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {problems.map((problem, index) => (
              <tr 
                key={problem.problem_id} 
                className="hover:bg-gray-50 transition-colors duration-150"
              >
                <td className="px-3 py-3 text-center">
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                    {index + 1}
                  </span>
                </td>
                <td className="px-3 py-3 text-center">
                  <span className="text-sm font-mono text-gray-600">
                    {problem.problem_id}
                  </span>
                </td>
                <td className="px-3 py-3">
                  <div className="flex justify-center gap-2">
                    <button
                      type="button"
                      className={`${buttonStyles.base} ${results[problem.problem_id] === true
                          ? buttonStyles.correct.active
                          : buttonStyles.correct.inactive
                        } flex items-center gap-1.5`}
                      onClick={() => setResults(prev => ({
                        ...prev,
                        [problem.problem_id]: true
                      }))}
                    >
                      <CheckCircle size={14} />
                      정답
                    </button>
                    <button
                      type="button"
                      className={`${buttonStyles.base} ${results[problem.problem_id] === false
                          ? buttonStyles.incorrect.active
                          : buttonStyles.incorrect.inactive
                        } flex items-center gap-1.5`}
                      onClick={() => setResults(prev => ({
                        ...prev,
                        [problem.problem_id]: false
                      }))}
                    >
                      <XCircle size={14} />
                      오답
                    </button>
                  </div>
                </td>
                <td className="px-3 py-3 text-center">
                  {solutions[problem.problem_id] ? (
                    <div className="inline-block rounded-lg bg-blue-50 px-3 py-1.5">
                      <InlineMath math={solutions[problem.problem_id]} />
                    </div>
                  ) : (
                    <span className="text-sm text-gray-400">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 진행 상태 바 */}
      {!isConfirmMode && (
        <div className="mx-4 mt-3 mb-2">
          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
            <span>진행률</span>
            <span className="font-medium">
              {answeredProblems} / {totalProblems} 완료
              {isAllProblemsAnswered() && (
                <span className="ml-2 text-green-600">✓ 모든 문제 채점 완료</span>
              )}
            </span>
          </div>
          <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ease-out ${
                isAllProblemsAnswered() ? 'bg-green-500' : 'bg-primary'
              }`}
              style={{ width: `${(answeredProblems / totalProblems) * 100}%` }}
            />
          </div>
        </div>
      )}
    </Modal>
  );
}

export default ResultInputModal; 